import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { API } from '@/api'
import { ServerDomainInfo } from '@/api/type-server.ts'
import {
  Button,
  ButtonRadio,
  Divider,
  Loading,
  message,
  Modal,
  Pagination,
  Trans,
} from '@/components'
import { EmptyView } from '@/components/EmptyView'
import { ItemConsumeType } from '@/consts/snapshot'
import { Snapshot, SnapStatus } from '@/models/snapshot'
import { useUserStore } from '@/stores/user'
import { formatBytes, formatDatePreset } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { EditableSnapshotName } from './components/EditableSnapshotName'
import { SnapshotRestoreCounter } from './components/SnapshotRestoreCounter'

interface FilterItem {
  label: string
  value: string
  codes: number[] | undefined
}

export function SnapshotListPage() {
  const [snapList, setSnapList] = useState<Snapshot[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const pageSize = 10
  const navigate = useNavigate()
  const userStore = useUserStore()
  const [domainInfos, setDomainInfos] = useState<ServerDomainInfo[]>([])
  const { t } = useTranslation()
  const [itemConsume, setItemConsume] = useState<{
    total: number
    used: number
  } | null>(null)

  const statusFilters: FilterItem[] = [
    {
      label: t('snapshot.type.available'),
      value: 'available',
      codes: [SnapStatus.Finished],
    },
    {
      label: t('snapshot.type.restorable'),
      value: 'restorable',
      codes: [SnapStatus.Deleting, SnapStatus.Resumable],
    },
  ]
  const [filter, setFilter] = useState<FilterItem | undefined>(statusFilters[0])

  const notAvailable = !userStore.partVolume?.quota_gb && snapList?.length === 0

  const getItemConsume = useCallback(async () => {
    const res = await API.getItemConsume({ type: ItemConsumeType.Snapshot })
    if (res.code === 0) {
      setItemConsume(res.info)
    }
  }, [])

  const getDomains = useCallback(async () => {
    setLoading(true)
    try {
      const res = await API.getDomainList()
      if (res.code === 0) {
        setDomainInfos(res.domainInfos)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  const getSnapshotList = useCallback(
    async (p = page) => {
      setLoading(true)
      try {
        const res = await API.getSnapList({
          domains: domainInfos.map((d) => d.domain),
          page: p,
          per_page: pageSize,
          status: filter?.codes,
        })
        if (res.code === 0) {
          setPage(res.pagination.page)
          setTotal(res.pagination.total)
          setSnapList(res.snapshots.map((snap) => new Snapshot(snap)))
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    },
    [page, pageSize, filter, domainInfos],
  )

  const onResumeSnapshot = async (resID: string) => {
    const remainingRestores = itemConsume?.total ? itemConsume.total - (itemConsume.used || 0) : 0

    if (remainingRestores <= 3 && remainingRestores > 0) {
      Modal.confirm({
        title: t('snapshot.restoreModal.title'),
        content: (
          <div className="flex-row">
            <div className="t-lg text-secondary">
              {remainingRestores === 1 ? (
                <Trans
                  i18nKey="snapshot.restoreModal.finalRestoreMessage"
                  components={{
                    1: <span className="text-error t-bold" />,
                  }}
                />
              ) : (
                <Trans
                  i18nKey="snapshot.restoreModal.lowRestoreMessage"
                  values={{ count: remainingRestores }}
                  components={{
                    1: <span className="text-error t-bold" />,
                  }}
                />
              )}
            </div>
          </div>
        ),
        confirmBtnCfg: {
          type: 'secondary',
          text: t('snapshot.restoreModal.confirmBtn'),
        },
        className: 'w-[640px]',
      }).then(() => {
        performRestore(resID)
      })
      return
    }

    performRestore(resID)
  }

  const performRestore = async (resID: string) => {
    setLoading(true)
    try {
      const res = await API.resumeSnapshot({
        res_id: resID,
        resume_snapshot: true,
      })
      if (res.code === 0) {
        getSnapshotList()
        getItemConsume()
      } else {
        setLoading(false)
        if (res.code === 177) {
          Modal.confirm({
            title: t('snapshot.cannotRestoreModal.title'),
            content: (
              <div className="flex-row">
                <div className="t-lg text-secondary">
                  <Trans
                    i18nKey="snapshot.cannotRestoreModal.content"
                    components={{
                      1: <a href="mailto:support@glows.ai" className="text-highlight" />,
                    }}
                  />
                </div>
              </div>
            ),
            confirmBtnCfg: {
              type: 'secondary',
              text: t('snapshot.cannotRestoreModal.confirmBtn'),
            },
            className: 'w-[480px]',
          })
        } else {
          Modal.confirm({
            title: t('snapshot.restoreFailedModal.title'),
            content: (
              <div className="flex-row">
                <div className="t-lg text-secondary flex items-center">
                  {t('snapshot.restoreFailedModal.content')}
                </div>
              </div>
            ),
            confirmBtnCfg: {
              type: 'secondary',
              text: t('snapshot.restoreFailedModal.confirmBtn'),
            },
            className: 'w-[480px]',
          }).then(() => {
            navigate('/space')
          })
        }
      }
    } catch (error) {
      setLoading(false)
    }
  }

  useEffect(() => {
    getDomains()
    getItemConsume()
  }, [])

  useEffect(() => {
    if (domainInfos.length > 0) {
      getSnapshotList(1)
    }
  }, [domainInfos, filter])

  return (
    <div className="card bg-white h-full overflow-y-auto">
      <div className="flex h-[60px] items-start justify-between">
        <div className="h6">
          <Trans i18nKey="snapshot.title">Snapshots</Trans>
        </div>
        <Link to="/create">
          <Button type="secondary">
            <Trans i18nKey="snapshot.createBtn">New Instance</Trans>
          </Button>
        </Link>
      </div>
      <Divider />

      <div className="flex justify-between items-center">
        <ButtonRadio
          value={filter?.value}
          items={statusFilters}
          onChange={(e) => {
            setFilter(statusFilters.find((i) => i.value === e))
          }}
        />

        <SnapshotRestoreCounter total={itemConsume?.total || 0} used={itemConsume?.used || 0} />
      </div>

      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}

      {!loading && !notAvailable && (
        <table className="table-card mt-4 table-fixed">
          <thead>
            <tr>
              <th colSpan={2}>
                <Trans i18nKey="snapshot.tableHeaderID">ID</Trans>
              </th>
              <th colSpan={3}>
                <Trans i18nKey="snapshot.tableHeaderName">Name</Trans>
              </th>
              <th colSpan={1}>
                <Trans i18nKey="snapshot.tableHeaderSize">Size</Trans>
              </th>
              <th colSpan={1}>
                <Trans i18nKey="snapshot.tableHeaderStatus">Status</Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="snapshot.tableHeaderTime">Create Time</Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="snapshot.tableHeaderAction">Action</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {snapList.map((snap) => (
              <tr key={snap.resID} className="group">
                <td colSpan={2} className="t-sm p-4">
                  {snap.resID}
                </td>
                <td colSpan={3} className="p-4">
                  <div className="flex items-center">
                    <EditableSnapshotName snapshot={snap} onUpdate={getSnapshotList} />
                  </div>
                </td>
                <td colSpan={1} className="t-sm p-4">
                  {formatBytes(snap.size)}
                </td>
                <td colSpan={1} className={cn('t-sm p-4', snap.statusText.style)}>
                  {snap.statusText.label}
                </td>
                <td colSpan={2} className="text-tertiary t-sm p-4">
                  {formatDatePreset(snap.createdAt, 'minute')}
                </td>
                <td colSpan={2} className="text-tertiary min-w-[220px] p-4">
                  <div className="flex gap-x-4">
                    <div
                      className={cn('t-sb text-highlight cursor-pointer', {
                        hidden: filter?.value === 'available',
                      })}
                      onClick={() => {
                        onResumeSnapshot(snap.resID)
                      }}
                    >
                      <Trans i18nKey="snapshot.actionRestore">Restore</Trans>
                    </div>
                    <div
                      className={cn('t-sb text-red-500 cursor-pointer')}
                      onClick={() => {
                        Modal.confirm({
                          title: t('snapshot.deleteModal.title'),
                          content: (
                            <div className="bg-panel-gray-100 rounded-lg p-4 flex-row">
                              <div className="t-sm font-medium text-secondary flex items-center">
                                {t('snapshot.deleteModal.content')}
                              </div>
                            </div>
                          ),
                          confirmBtnCfg: {
                            type: 'danger',
                            text: t('snapshot.deleteModal.confirmBtn'),
                          },
                          className: 'w-[640px]',
                        }).then(() => {
                          API.deleteSnapshot({ res_id: snap.resID }).then(() => {
                            message.success(t('snapshot.deleteModal.successMsg'))
                            getSnapshotList()
                          })
                        })
                      }}
                    >
                      <Trans i18nKey="snapshot.actionDelete">Delete</Trans>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!loading && !notAvailable && (
        <Pagination
          size={pageSize}
          total={total}
          current={page}
          className="justify-end mt-4"
          onChange={(page) => getSnapshotList(page)}
        />
      )}
      {!loading && notAvailable && (
        <div className="mt-4 mb-2 ">
          <EmptyView
            title={
              <Trans i18nKey="snap.insufficientQuotaAvailableTitle">
                Insufficient quota available
              </Trans>
            }
            content={
              <Trans i18nKey="snap.insufficientQuotaAvailableContent">
                Update your quota settings in Storage Capacity to proceed
              </Trans>
            }
            action={
              <Button type="secondary" onClick={() => navigate('/space')}>
                <Trans i18nKey="snapshots.emptyViewActionBtn">Manage Capacity</Trans>
              </Button>
            }
          />
        </div>
      )}
    </div>
  )
}
